import { Component, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { QuoteToolsService } from '../../pos/quotes/services/quote-tools.service';
import { SignalrConnectionType } from '../entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../entities/signalrMethodName.enum';
import { SignalrNotification } from '../entities/SignalrNotification.class';
import { SignalrProgressState } from '../entities/signalrProgressState.enum';
import { AppLocalization } from '../entities/app-localization';
import { ImportItemsQuoteService } from './import-items-quote.service';


@Component({
  selector: 'app-import-quotes-and-orders',
  templateUrl: './import-quotes-and-orders.component.html',
  styleUrls: ['./import-quotes-and-orders.component.scss'],
  standalone: false
})
export class ImportQuotesAndOrdersComponent implements OnInit {
  private subscriptionSignalR = new Subscription();
  private subscriptionConsigned = new Subscription();
  private subscriptionProductsCount = new Subscription();
  private subscriptionImportItemsQuote = new Subscription();
  appStrings: AppLocalization;
  isImportEnabled = false;
  sourceQuote;
  destinationQuote: string;
  shouldDisplayImport = false;
  importLoading: boolean;
  isConsigned: boolean;
  private productCount;
  quoteId: string;
  selectedQuoteId: string;
  checkSelectedQuoteForConsignedItemsLoading: boolean;

  importActivePages = { quotes: '/quotes', orders: '/orders' };

  constructor(
    public appInfoService: AppInfoService,
    private router: Router,
    private quoteToolsService: QuoteToolsService,
    private signalrService: SignalRService,
    private notificationService: NotificationService,
    private importItemsQuoteService: ImportItemsQuoteService
  ) { }

  ngOnInit(): void {
    this.shouldDisplayImportQuotesOrders();
    this.appStrings = this.appInfoService.appStrings;
    this.subscriptionProductsCount.add(this.quoteToolsService.productCount$
      .subscribe(productsNumber => this.productCount = productsNumber));
    this.subscriptionSignalR.add(this.signalrService.importQuotesOrOrdersData$
      .subscribe(progress => this.importQuotesOrOrdersSetProgress(progress)));
    // We listen to hasProductsFromConsigned event, to find out if a user is or isn't consigned
    this.subscriptionConsigned.add(this.quoteToolsService.hasProductsFromConsigned$
      .subscribe((result: boolean) => {
        this.isConsigned = result;
        this.checkImportEnabled(window.location.href);
      }));

    // Subscribe to destination quote changes
    this.subscriptionImportItemsQuote.add(this.importItemsQuoteService.getDestinationQuoteId$()
      .subscribe(() => {
        this.shouldDisplayImportQuotesOrders();
      }));

    // subscribe to routing
    this.router.events.subscribe((triggeredEvent) => {
      if (triggeredEvent instanceof NavigationStart) {
        this.isConsigned = true;
        const url = triggeredEvent.url;
        // Regex to take the quote id
        const quoteIdRegex = /([^/]*)$/g;
        this.sourceQuote = url.match(quoteIdRegex)[0];
      }
    });

    this.router.events.subscribe((triggeredEvent) => {
      if (triggeredEvent instanceof NavigationEnd) {
        const url = triggeredEvent.url;
        this.checkImportEnabled(url);
        this.shouldDisplayImportQuotesOrders();
      }
    });

    this.subscriptionImportItemsQuote.add(this.importItemsQuoteService.getSelectedQuoteId$()
      .subscribe(selectedQuoteId => {
        this.selectedQuoteId = selectedQuoteId;
        // Check if selected quote has consigned items
        if (selectedQuoteId) {
          this.checkSelectedQuoteForConsignedItems(selectedQuoteId);
        }
      }));
  }

  // This method checks if the users is inside or outside a quote page
  // Also, here we check if the quote is consigned or not
  // For consigned quotes, the disabled-cart buttons should be displayed
  checkImportEnabled(url) {
    const regex = /\/quotes\/.*/;
    if ((url.match(regex) || !!this.selectedQuoteId) && !this.isConsigned) {
      this.isImportEnabled = true;
    } else {
      this.isImportEnabled = false;
    }
  }

  importQuotesOrders() {
    if (!this.productCount) {
      this.notificationService.showNotification(this.appStrings.importProductsFromNewQuote_error, NotificationType.Error);
      return;
    }
    this.importLoading = true;
    if (this.sourceQuote === 'new') {
      this.sourceQuote = this.quoteToolsService.quoteId;
    }

    this.signalrService.startConnection(SignalrConnectionType.ImportQuotesOrOrders)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.importQuotesOrOrders
        );

        const payload = {
          sourceQuoteId: this.selectedQuoteId || this.sourceQuote,
          destinationQuoteId: this.destinationQuote,
          notification: signalrNotification
        };
        this.quoteToolsService.importQuotesOrders(payload).subscribe(() => {
          // This is intentional
        }, () => {
          // This is intentional
        });
        this.shouldDisplayImport = false;
      });
  }

  // Removes the quoteId from local storage
  removeQuoteIdFromLocalStorage() {
    this.importItemsQuoteService.removeDestinationQuoteId();
  }

  private importQuotesOrOrdersSetProgress(progress) {
    if (progress.state === SignalrProgressState.Done) {
      this.quoteToolsService.refreshToolsList$.next(undefined);
      this.signalrService.closeConnection();
      const destinationQuote = this.destinationQuote;
      this.importItemsQuoteService.removeDestinationQuoteId();
      this.router.navigate(['quotes/', destinationQuote])
        .then(() => setTimeout(() => this.updateSummaryAndRefresh(destinationQuote)));
    }

    if (progress.state === SignalrProgressState.Error) {
      this.signalrService.closeConnection();
      this.notificationService.showNotification(progress.message as string, NotificationType.Error, true);
      this.importLoading = false;
      this.shouldDisplayImportQuotesOrders();
    }
  }

  private updateSummaryAndRefresh(destinationQuoteId: string) {
    // subscribe to payment summary to know when it's done
    const sub = this.quoteToolsService.paymentSummaryUpdated$.subscribe(finished => {
      if (!finished) {
        return;
      }

      this.quoteToolsService.priceMatrixListRefresh$.next(undefined);

      // After importing the date, the destination quote is going to be cleared
      // The buttons won't be displayed anymore, until the user will import another quote
      this.importLoading = false;
      this.removeQuoteIdFromLocalStorage();
      this.shouldDisplayImportQuotesOrders();
      sub.unsubscribe();
    });

    // Ensure the service has the destination quote ID so toolUpdateCallback uses the
    // correct quote, regardless of any concurrent checkSelectedQuoteForConsignedItems calls.
    this.quoteToolsService.setQuoteId(destinationQuoteId);
    // trigger payment summary update
    this.quoteToolsService.productListHasChanges$.next(true);
  }

  shouldDisplayImportQuotesOrders() {
    this.destinationQuote = this.importItemsQuoteService.getDestinationQuoteId();
    // Only show import UI if we have a destination quote AND we're on an import-available page
    this.shouldDisplayImport = !!this.destinationQuote && this.isImportAvailablePage();
  }

  /**
   * Checks if the current URL is a page where import functionality should be available
   * @returns true if import should be available on this page
   */
  private isImportAvailablePage(): boolean {
    const url = this.router.url || window.location.pathname;
    return this.isImportAvailablePageByUrl(url);
  }

  /**
   * Checks if a given URL is a page where import functionality should be available
   * @param url - The URL to check
   * @returns true if import should be available on this page
   */
  private isImportAvailablePageByUrl(url: string): boolean {
    if (url.endsWith('/new')) {
      return false;
    }

    return url === this.importActivePages.quotes ||
      url.startsWith(`${this.importActivePages.quotes}/`) ||
      url === this.importActivePages.orders ||
      url.startsWith(`${this.importActivePages.orders}/`);
  }

  goToInitialQuote() {
    this.router.navigate(['quotes/', this.destinationQuote]);
    setTimeout(() => {
      this.removeQuoteIdFromLocalStorage();
      this.shouldDisplayImportQuotesOrders();
    });
  }

  /**
   * Checks if the selected quote for import has items from consigned warehouse
   * @param selectedQuoteId - The quote ID to check
   */
  private checkSelectedQuoteForConsignedItems(selectedQuoteId: string) {
    const currentQuoteId = this.quoteToolsService.quoteId;

    // Set the selected quote ID to load its products
    this.quoteToolsService.setQuoteId(selectedQuoteId);
    this.checkSelectedQuoteForConsignedItemsLoading = true;

    // Load the first page of products to check for consigned items
    this.quoteToolsService.getProductList(0, null, null, () => {
      this.checkSelectedQuoteForConsignedItemsLoading = false;
      // After products are loaded, restore the original quoteId
      this.quoteToolsService.setQuoteId(currentQuoteId);
    });
  }
}
